import classNames from 'classnames';
import { FC, useCallback, useState } from 'react';

import Button from '../common/button';
import { LazyImage } from '../common/lazy-image';

const makeUrl = (url: string) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${import.meta.env.VITE_REACT_APP_API_URL}/${url}`;
};

export const MacCardList: FC<{
  cards: {
    id: number;
    img: string;
    selected: boolean;
    show: boolean;
    deck: string;
    deckCover: string;
    deckColor?: string;
  }[];
  selectedCards: {
    id: number;
    img: string;
    selected: boolean;
  }[];
  setSelectedCards: (
    a: {
      id: number;
      img: string;
      selected: boolean;
    }[],
  ) => void;
  /** Идентификатор участника — отправляется с выбором, чтобы у каждого клиента был свой выбор */
  participantIdentity?: string;
  /** Отправить выбор по data channel */
  onSubmitViaDataChannel?: (result: Array<{ id: number; img: string; selected: boolean }>) => void;
  /** Групповая терапия: показывать сетку карт в 6 столбцов */
  isGroupTherapy?: boolean;
}> = ({ cards, setSelectedCards, selectedCards, participantIdentity, onSubmitViaDataChannel, isGroupTherapy }) => {
  const [activeItems, setActiveItems] = useState<number[]>([]);

  const handleChooseCard = useCallback(
    (id: number) => {
      if (selectedCards.filter((el) => el.selected).length > 0) return;
      if (activeItems.length === 4 && !activeItems.includes(id)) return;
      if (activeItems.includes(id)) {
        setActiveItems((prev) => prev.filter((el) => el !== id));

        return;
      }
      setActiveItems([...activeItems, id]);
    },
    [activeItems, selectedCards],
  );

  const getChooseToPsy = useCallback(() => {
    const arr = cards.map((el) => {
      if (activeItems.includes(el.id)) {
        return { ...el, selected: !el.selected };
      } else {
        return el;
      }
    });
    const selected = arr.filter((el) => el.selected);

    onSubmitViaDataChannel?.(selected);
    setSelectedCards(selected);
  }, [activeItems, cards, setSelectedCards, onSubmitViaDataChannel]);

  return (
    <div
      className={classNames(
        'flex flex-col h-full min-h-0 w-full rounded-[5px] md:rounded-[15px] overflow-hidden px-4 py-4',
        isGroupTherapy ? 'bg-white relative pb-0' : 'bg-grey1',
      )}
    >
      <h2
        className={classNames(
          'font-comfortaa font-bold text-2xl shrink-0 mb-1',
          isGroupTherapy ? 'text-[#2C3038]' : 'text-white',
        )}
      >
        МАК-карты
      </h2>
      <p
        className={classNames(
          'font-manrope mb-3 shrink-0',
          isGroupTherapy ? 'text-lg text-[#585858]' : 'text-[14px] lg:text-lg text-white',
        )}
      >
        Выберите максимум 4 карты из колоды
      </p>

      {/* В групповой терапии кнопка поверх (absolute), карты с нормальными пропорциями 110:140 и скроллом */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 white-scroll">
        <div
          className={classNames(
            'grid gap-3 auto-rows-fr w-full min-w-0',
            isGroupTherapy ? 'grid-cols-2 lg:grid-cols-6' : 'grid-cols-2 lg:grid-cols-3',
          )}
        >
          {cards.map((item) => {
            if (item.show || activeItems.includes(item.id)) {
              return (
                <div
                  className={classNames(
                    'rounded-[14px] overflow-hidden border-2 aspect-[110/140] cursor-pointer',
                    activeItems.includes(item.id) ? 'border-[#DEC05E]' : 'border-[#6A6B70]',
                  )}
                  onClick={(e) => {
                    const target = e.target as HTMLElement;
                    if (target.closest('button')) return;
                    handleChooseCard(item.id);
                  }}
                  key={item.id}
                >
                  <div className="w-full h-full">
                    <LazyImage
                      src={makeUrl(item.img)}
                      allowZoom={true}
                      className="w-full h-full object-cover"
                      alt={String(item.id)}
                    />
                  </div>
                </div>
              );
            }
            const deckGroup = cards.find((el) => el.deck === item.deck);
            const deckCover = deckGroup?.deckCover || '';
            const borderColor = deckGroup?.deckColor || '#6A6B70';

            return (
              <div
                className="rounded-[14px] overflow-hidden border-2 aspect-[110/140] cursor-pointer"
                style={{ borderColor }}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest('button')) return;
                  handleChooseCard(item.id);
                }}
                key={item.id}
              >
                <div className="w-full h-full">
                  <LazyImage
                    src={makeUrl(deckCover)}
                    allowZoom={false}
                    className="w-full h-full object-cover"
                    alt="deck cover"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div
        className={classNames(
          'flex justify-center shrink-0',
          isGroupTherapy ? 'absolute bottom-4 left-1/2 -translate-x-1/2 z-10' : 'pt-3',
        )}
      >
        <Button
          className={classNames(
            '!text-sm lg:!text-base p-[6px_10px]',
            !isGroupTherapy && '!text-white',
            isGroupTherapy && '!w-[200px]',
          )}
          onClick={() => getChooseToPsy()}
          disabled={activeItems.length === 0}
        >
          Выбрать
        </Button>
      </div>
    </div>
  );
};
